"use client";

import Link from "next/link";
import { TreasuryVisual } from "@/components/landing/ModuleVisuals";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Feature {
  titleKey: string;
  descKey: string;
}

export default function TreasuryProductPage() {
  const { t } = useI18n();

  const features: Feature[] = [
    {
      titleKey: "pages.product.treasury.features.requests.title",
      descKey: "pages.product.treasury.features.requests.desc",
    },
    {
      titleKey: "pages.product.treasury.features.approval.title",
      descKey: "pages.product.treasury.features.approval.desc",
    },
    {
      titleKey: "pages.product.treasury.features.forecast.title",
      descKey: "pages.product.treasury.features.forecast.desc",
    },
    {
      titleKey: "pages.product.treasury.features.calendar.title",
      descKey: "pages.product.treasury.features.calendar.desc",
    },
    {
      titleKey: "pages.product.treasury.features.notifications.title",
      descKey: "pages.product.treasury.features.notifications.desc",
    },
    {
      titleKey: "pages.product.treasury.features.export.title",
      descKey: "pages.product.treasury.features.export.desc",
    },
  ];

  const benefits = [
    {
      titleKey: "pages.product.treasury.benefits.time.title",
      descKey: "pages.product.treasury.benefits.time.desc",
      value: "15 часов/неделя",
    },
    {
      titleKey: "pages.product.treasury.benefits.errors.title",
      descKey: "pages.product.treasury.benefits.errors.desc",
      value: "-95% ошибок",
    },
    {
      titleKey: "pages.product.treasury.benefits.visibility.title",
      descKey: "pages.product.treasury.benefits.visibility.desc",
      value: "100% видимость",
    },
    {
      titleKey: "pages.product.treasury.benefits.decisions.title",
      descKey: "pages.product.treasury.benefits.decisions.desc",
      value: "Быстрые решения",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
              {t("pages.product.treasury.headline") ||
                "Казначейство. Просто и прозрачно."}
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.treasury.description") ||
                `Автоматизируйте платежи, согласования и прогнозирование.
                От заявки до утверждения — всё в одном месте.
                Экономьте часы, снижайте ошибки, контролируйте кассу.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild variant="default" className="h-12">
                <Link href="/contact">
                  {t("pages.product.treasury.cta") || "Попробовать бесплатно"}
                </Link>
              </Button>
              <Button asChild variant="outline" className="h-12">
                <Link href="/pricing">
                  {t("pages.product.treasury.pricing") || "Смотреть тарифы"}
                </Link>
              </Button>
            </div>
          </div>

          {/* Visual */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden h-96">
            <TreasuryVisual />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12">
          {t("pages.product.treasury.featuresTitle") || "Основные возможности"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, idx) => (
            <div key={idx} className="border border-slate-200 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <CheckCircle2 size={24} className="text-keiro-green flex-shrink-0 mt-0.5" />
                <h3 className="font-bold text-slate-900">
                  {t(feature.titleKey) || feature.titleKey}
                </h3>
              </div>
              <p className="text-slate-600">
                {t(feature.descKey) || feature.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.treasury.benefitsTitle") ||
            "Результаты, которые видны сразу"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, idx) => (
            <div
              key={idx}
              className="bg-keiro-mist rounded-xl p-8 border border-keiro-green/20 text-center"
            >
              <p className="text-sm text-slate-600 mb-3 uppercase tracking-wide font-semibold">
                {t(benefit.titleKey) || benefit.titleKey}
              </p>
              <p className="text-3xl font-bold text-keiro-green-dark mb-3">
                {benefit.value}
              </p>
              <p className="text-sm text-slate-600">
                {t(benefit.descKey) || benefit.descKey}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Integration Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              {t("pages.product.treasury.integrationTitle") ||
                "Интегрируется с 1С и другими системами"}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              {t("pages.product.treasury.integrationDesc") ||
                `Данные автоматически синхронизируются с 1С:Отель
                и другими системами. Нет ручного ввода — всё работает.`}
            </p>
            <ul className="space-y-3">
              {[
                "1С:Отель и 1С:БП",
                "Logus HMS",
                "TravelLine",
                "Opera PMS",
                "iiko POS",
              ].map((sys) => (
                <li key={sys} className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-keiro-green rounded-full" />
                  <span className="text-slate-700">{sys}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
            <p className="text-slate-600 text-center">
              {t("pages.product.treasury.integrationPlaceholder") ||
                "Интеграционная архитектура"}
            </p>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-t border-slate-200">
        <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
          {t("pages.product.treasury.useCasesTitle") ||
            "Типовые сценарии"}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              title:
                t("pages.product.treasury.useCase1.title") ||
                "Сетевой отель (5-20 объектов)",
              desc:
                t("pages.product.treasury.useCase1.desc") ||
                "Централизованное управление платежами всех объектов. Консолидированный прогноз кассовых потоков.",
              emoji: "🏨",
            },
            {
              title:
                t("pages.product.treasury.useCase2.title") ||
                "Независимый отель",
              desc:
                t("pages.product.treasury.useCase2.desc") ||
                "Полная видимость над расходами. Быстрые решения на заявки, меньше времени на согласования.",
              emoji: "🔑",
            },
            {
              title:
                t("pages.product.treasury.useCase3.title") ||
                "Казначей / CFO",
              desc:
                t("pages.product.treasury.useCase3.desc") ||
                "Все данные в одном месте. Мониторинг платежей, аналитика, аудит за 5 минут.",
              emoji: "📊",
            },
          ].map((useCase, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-xl p-6 hover:border-keiro-green/50 transition-colors"
            >
              <div className="text-3xl mb-3">{useCase.emoji}</div>
              <h3 className="font-bold text-slate-900 mb-3">{useCase.title}</h3>
              <p className="text-slate-600">{useCase.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.product.treasury.ctaTitle") ||
              "Готовы упростить казначейство?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.product.treasury.ctaDesc") ||
              "14 дней бесплатного доступа, без кредитной карты"}
          </p>
          <Button asChild variant="default" className="h-12 px-8">
            <Link href="/contact">
              {t("pages.product.treasury.ctaButton") || "Начать бесплатно"}
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
